/* Made-to-measure configurator — Cadar Atelier 1.1
   Depends on theme.js for cart + money helpers.
   Changes vs 1.0.1: parametric shapes (corner C/D, cut-out, taper, rounded R,
   two widths, modules), vehicle/boat library, live weight + delivery method,
   split with firmness per side, sleeper-weight hints, length tiers over 200 cm,
   non-return acknowledgement, notes for the cutter. */
(function () {
  'use strict';

  var UNITS = { cm: 1, mm: 10, in: 1 / 2.54 };
  var COVER_KG_PER_M2 = 0.9;      /* quilted cover + border, both faces */
  var PARCEL_MAX_KG = 31.5;        /* DPD UK */
  var PARCEL_MAX_LEN = 175;        /* DPD UK, cm */
  var LIFT_BED_WARN_KG = 15;       /* gas-strut beds complain above this */

  function money(cents) {
    return window.themeFormatMoney ? window.themeFormatMoney(cents) : (cents / 100).toFixed(2);
  }

  function init(root) {
    var dataEl = document.querySelector('[data-configurator-data="' + root.closest('[data-section-id]').dataset.sectionId + '"]');
    if (!dataEl) return;
    var D;
    try { D = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var form = root.querySelector('[data-configurator-form]');
    var drawing = root.querySelector('[data-drawing]');
    var totalEl = root.querySelector('[data-price-total]');
    var linesEl = root.querySelector('[data-price-lines]');
    var bandPill = root.querySelector('[data-band-pill]');
    var submit = root.querySelector('[data-configurator-submit]');
    var errorEl = root.querySelector('[data-configurator-error]');
    var ack = root.querySelector('[data-ack]');
    var vehicleSel = root.querySelector('[data-vehicle]');

    var unit = 'cm';
    var dims = { w: null, l: null, d: null };
    var valid = { w: false, l: false, d: false };
    var params = {};  /* shape parameters in cm */

    function toCm(value) { return unit === 'in' ? value * 2.54 : value / UNITS[unit]; }
    function fromCm(cm) {
      var v = unit === 'in' ? cm / 2.54 : cm * UNITS[unit];
      return unit === 'in' ? Math.round(v * 10) / 10 : Math.round(v);
    }
    function fill(template, map) {
      return Object.keys(map).reduce(function (acc, key) { return acc.split(key).join(map[key]); }, template);
    }
    function q(sel) { return root.querySelector(sel); }
    function setText(sel, text) { var el = q(sel); if (el) el.textContent = text; }

    /* ---- validation ---- */
    var LIM = D.limits;
    var SPEC = {
      w: { min: LIM.minW, max: LIM.maxW },
      l: { min: LIM.minL, max: LIM.maxL },
      d: { min: LIM.minD, max: LIM.maxD }
    };

    function readDim(key) {
      var input = form.querySelector('[data-dim-input="' + key + '"]');
      var wrap = form.querySelector('[data-dim="' + key + '"]');
      var hint = form.querySelector('[data-dim-hint="' + key + '"]');
      var raw = parseFloat(input.value);
      var spec = SPEC[key];
      var defaultHint = key === 'd'
        ? fill(D.strings.rangeHint, { '__MIN__': fromCm(spec.min), '__MAX__': fromCm(spec.max), '__U__': D.strings[unit] })
        : fill(D.strings.maxHint, { '__V__': fromCm(spec.max), '__U__': D.strings[unit] });

      if (isNaN(raw)) {
        valid[key] = false; dims[key] = null;
        wrap.classList.remove('is-invalid'); input.removeAttribute('aria-invalid');
        hint.textContent = defaultHint; return;
      }
      var cm = Math.round(toCm(raw));
      if (cm < spec.min || cm > spec.max) {
        valid[key] = false; dims[key] = cm;
        wrap.classList.add('is-invalid'); input.setAttribute('aria-invalid', 'true');
        hint.textContent = fill(cm < spec.min ? D.strings.errorMin : D.strings.errorMax,
          { '__V__': fromCm(cm < spec.min ? spec.min : spec.max), '__U__': D.strings[unit] });
      } else {
        valid[key] = true; dims[key] = cm;
        wrap.classList.remove('is-invalid'); input.removeAttribute('aria-invalid');
        hint.textContent = defaultHint;
      }
    }

    /* shape parameters: every [data-param] input, converted to cm, clamped to the plan */
    function readParams(shapeKey) {
      params = {};
      form.querySelectorAll('[data-param-for]').forEach(function (wrap) {
        wrap.hidden = wrap.getAttribute('data-param-for').split(',').indexOf(shapeKey) === -1;
      });
      form.querySelectorAll('[data-param]').forEach(function (input) {
        var key = input.getAttribute('data-param');
        var wrap = input.closest('[data-param-for]');
        var applies = wrap && !wrap.hidden;
        if (!applies) return;
        var raw = parseFloat(input.value);
        if (isNaN(raw)) return;
        params[key] = key === 'mods' ? Math.max(2, Math.min(3, Math.round(raw))) : Math.round(toCm(raw));
      });
      /* clamp so the drawing never inverts */
      if (dims.w && dims.l) {
        if (params.c) params.c = Math.min(params.c, dims.l - 10);
        if (params.d) params.d = Math.min(params.d, dims.w - 10);
        if (params.nw) params.nw = Math.min(params.nw, dims.w - 10);
        if (params.nh) params.nh = Math.min(params.nh, dims.l - 10);
        if (params.tw) params.tw = Math.min(params.tw, dims.w);
        if (params.w2) params.w2 = Math.min(params.w2, dims.w);
        if (params.r) params.r = Math.min(params.r, Math.floor(Math.min(dims.w, dims.l) / 2));
      }
    }

    /* ---- selection helpers ---- */
    function checked(name) { return form.querySelector('input[name="' + name + '"]:checked'); }
    function bandFor(w, l) {
      for (var i = 0; i < D.bands.length; i++) if (w <= D.bands[i].maxW && l <= D.bands[i].maxL) return D.bands[i];
      return D.bands[D.bands.length - 1] || null;
    }
    function depthTierFor(d) {
      for (var i = 0; i < D.depths.length; i++) if (d <= D.depths[i].maxD) return D.depths[i];
      return D.depths[D.depths.length - 1] || null;
    }
    function lengthTierFor(l) {
      var hit = null;
      (D.lengthTiers || []).forEach(function (t) { if (l > t.overL && (!hit || t.overL > hit.overL)) hit = t; });
      return hit;
    }
    function findVariant(bandValue, foamValue, depthValue) {
      return D.variants.find(function (v) {
        return v.options[D.optionIndex.band] === bandValue &&
               v.options[D.optionIndex.foam] === foamValue &&
               v.options[D.optionIndex.depth] === depthValue;
      });
    }
    function foamCfg(value) { return (D.foams || []).find(function (f) { return f.value === value; }) || {}; }

    /* ---- weight + delivery ---- */
    function estimate(w, l, d, foam, shapeKey) {
      if (!w || !l || !d) return null;
      var areaM2 = (w * l) / 10000;
      if (shapeKey === 'corner' && params.c && params.d) areaM2 -= (params.c * params.d) / 20000;
      if (shapeKey === 'cutout' && params.nw && params.nh) areaM2 -= (params.nw * params.nh) / 10000;
      if (shapeKey === 'taper' && params.tw) areaM2 = ((w + params.tw) / 2 * l) / 10000;
      if (shapeKey === 'round') areaM2 = Math.PI * (w / 200) * (w / 200);
      var density = foam.density || 35;
      var kg = areaM2 * (d / 100) * density + areaM2 * COVER_KG_PER_M2;
      var rollable = foam.rollable !== false && d <= (foam.maxRollDepth || 25);
      var pieces = shapeKey === 'modules' ? (params.mods || 2) : (checked('cfg-split') ? 2 : 1);
      var pkgLen = Math.min(w, l);  /* rolled across the long side */
      var method;
      if (!rollable) method = 'twoman';
      else if (pkgLen > PARCEL_MAX_LEN || kg / pieces > PARCEL_MAX_KG) method = 'pallet';
      else method = 'parcel';
      return { kg: Math.round(kg * 10) / 10, areaM2: areaM2, rollable: rollable, method: method, pieces: pieces };
    }

    /* ---- drawing ---- */
    function draw(w, l, d, shapeKey) {
      if (!w || !l || !d) { drawing.innerHTML = ''; return; }
      var availW = 320, availH = 280;
      var sc = Math.min(availW / w, availH / l);
      var px = w * sc, py = l * sc, cx = 250, x0 = cx - px / 2, y0 = 40 + (availH - py) / 2;
      var ink = 'var(--text)', faint = 'var(--text-muted)', acc = 'var(--accent)';
      var path, extra = '';

      if (shapeKey === 'corner') {
        var c = (params.c || l * 0.24) * sc, dd = (params.d || w * 0.28) * sc;
        path = '<path d="M' + x0 + ' ' + y0 + 'h' + px + 'v' + (py - c) + 'l-' + dd + ' ' + c + 'h-' + (px - dd) + 'z"/>';
        extra += dimLine(x0 + px + 14, y0 + py - c, x0 + px + 14, y0 + py, (params.c || Math.round(l * 0.24)) + ' cm', ink, true);
        extra += dimLine(x0 + px - dd, y0 + py + 14, x0 + px, y0 + py + 14, (params.d || Math.round(w * 0.28)) + ' cm', ink, false);
      } else if (shapeKey === 'cutout') {
        var nw = (params.nw || w * 0.34) * sc, nh = (params.nh || l * 0.28) * sc;
        path = '<path d="M' + x0 + ' ' + y0 + 'h' + (px - nw) + 'v' + nh + 'h' + nw + 'v' + (py - nh) + 'h-' + px + 'z"/>';
        extra += dimLine(x0 + px - nw, y0 - 14, x0 + px, y0 - 14, (params.nw || Math.round(w * 0.34)) + ' cm', ink, false);
        extra += dimLine(x0 + px + 14, y0, x0 + px + 14, y0 + nh, (params.nh || Math.round(l * 0.28)) + ' cm', ink, true);
      } else if (shapeKey === 'taper') {
        var tw = (params.tw || w * 0.56) * sc;
        path = '<path d="M' + (cx - tw / 2) + ' ' + y0 + 'h' + tw + 'l' + ((px - tw) / 2) + ' ' + py + 'h-' + px + 'z"/>';
        extra += dimLine(cx - tw / 2, y0 - 14, cx + tw / 2, y0 - 14, (params.tw || Math.round(w * 0.56)) + ' cm', ink, false);
      } else if (shapeKey === 'round') {
        path = '<ellipse cx="' + cx + '" cy="' + (y0 + py / 2) + '" rx="' + (px / 2) + '" ry="' + (py / 2) + '"/>';
      } else if (shapeKey === 'rounded') {
        var r = (params.r || 20) * sc;
        path = '<rect x="' + x0 + '" y="' + y0 + '" width="' + px + '" height="' + py + '" rx="' + r + '"/>';
        extra += '<path d="M' + (x0 + r) + ' ' + y0 + 'v' + r + 'h-' + r + '" fill="none" stroke="' + acc + '" stroke-width=".8" stroke-dasharray="3 2"/>';
        extra += label(x0 + r + 6, y0 + r + 4, 'R ' + (params.r || 20), acc, 'start', 11);
      } else if (shapeKey === 'twowidth') {
        var w2 = (params.w2 || w * 0.8) * sc, split = (params.c || l * 0.5) * sc, inset = (px - w2) / 2;
        path = '<path d="M' + x0 + ' ' + y0 + 'h' + px + 'v' + split + 'l-' + inset + ' 0v' + (py - split) + 'h-' + w2 + 'v-' + (py - split) + 'l-' + inset + ' 0z"/>';
        extra += dimLine(x0 + inset, y0 + py + 14, x0 + inset + w2, y0 + py + 14, (params.w2 || Math.round(w * 0.8)) + ' cm', ink, false);
        extra += dimLine(x0 - 52, y0, x0 - 52, y0 + split, (params.c || Math.round(l * 0.5)) + ' cm', ink, true);
      } else if (shapeKey === 'modules') {
        var n = params.mods || 2, mw = px / n;
        path = '<rect x="' + x0 + '" y="' + y0 + '" width="' + px + '" height="' + py + '" rx="4"/>';
        for (var m = 1; m < n; m++) extra += '<line x1="' + (x0 + mw * m) + '" y1="' + y0 + '" x2="' + (x0 + mw * m) + '" y2="' + (y0 + py) + '" stroke="' + acc + '" stroke-width="1.2" stroke-dasharray="6 3"/>';
      } else {
        path = '<rect x="' + x0 + '" y="' + y0 + '" width="' + px + '" height="' + py + '" rx="4"/>';
      }

      /* split line, firmness per side */
      if (shapeKey !== 'modules' && checked('cfg-split')) {
        extra += '<line x1="' + cx + '" y1="' + y0 + '" x2="' + cx + '" y2="' + (y0 + py) + '" stroke="' + acc + '" stroke-width="1.6" stroke-dasharray="6 3"/>';
        var fl = checked('cfg-firmness'), fr = checked('cfg-firmness-r');
        extra += label(cx - px / 4, y0 + py / 2 + 4, (fl ? fl.value : '').toUpperCase(), faint, 'middle', 10, 1.2);
        extra += label(cx + px / 4, y0 + py / 2 + 4, (fr ? fr.value : '').toUpperCase(), faint, 'middle', 10, 1.2);
      }

      var s = '<svg viewBox="0 0 500 480" role="img" aria-label="' + w + ' × ' + l + ' × ' + d + ' cm">';
      s += '<g fill="var(--bg-alt)" stroke="' + ink + '" stroke-width="1.6" stroke-linejoin="round">' + path + '</g>';
      s += '<line x1="' + cx + '" y1="' + (y0 - 8) + '" x2="' + cx + '" y2="' + (y0 + py + 8) + '" stroke="' + acc + '" stroke-width=".8" stroke-dasharray="9 4 2 4" opacity=".65"/>';
      s += extra;
      s += label(x0, y0 - 22, D.strings.head.toUpperCase(), faint, 'start', 9.5, 1.3);
      s += label(x0 + px + 8, y0 + py + 18, D.strings.foot.toUpperCase(), faint, 'start', 9.5, 1.3);

      var dy = y0 + py + 34;
      s += tick(x0, y0 + py + 4, x0, dy + 7, faint) + tick(x0 + px, y0 + py + 4, x0 + px, dy + 7, faint);
      s += '<line x1="' + x0 + '" y1="' + dy + '" x2="' + (x0 + px) + '" y2="' + dy + '" stroke="' + ink + '" stroke-width="1"/>';
      s += slash(x0, dy, ink) + slash(x0 + px, dy, ink);
      s += '<rect x="' + (cx - 38) + '" y="' + (dy - 11) + '" width="76" height="20" fill="var(--surface)"/>';
      s += label(cx, dy + 5, w + ' cm', ink, 'middle');

      var dx = x0 - 38;
      s += tick(x0 - 4, y0, dx - 7, y0, faint) + tick(x0 - 4, y0 + py, dx - 7, y0 + py, faint);
      s += '<line x1="' + dx + '" y1="' + y0 + '" x2="' + dx + '" y2="' + (y0 + py) + '" stroke="' + ink + '" stroke-width="1"/>';
      s += slash(dx, y0, ink) + slash(dx, y0 + py, ink);
      s += '<g transform="translate(' + dx + ',' + (y0 + py / 2) + ') rotate(-90)"><rect x="-38" y="-11" width="76" height="20" fill="var(--surface)"/>' +
           '<text text-anchor="middle" y="5" font-family="ui-monospace, monospace" font-size="13" fill="' + ink + '">' + l + ' cm</text></g>';

      var eh = Math.max(d * sc, 10), ey = 390;
      s += label(x0, ey - 12, D.strings.elevation.toUpperCase(), faint, 'start', 9.5, 1.3);
      s += '<rect x="' + x0 + '" y="' + ey + '" width="' + px + '" height="' + eh + '" rx="3" fill="var(--bg-alt)" stroke="' + ink + '" stroke-width="1.4"/>';
      var dx2 = x0 + px + 26;
      s += '<line x1="' + dx2 + '" y1="' + ey + '" x2="' + dx2 + '" y2="' + (ey + eh) + '" stroke="' + ink + '" stroke-width="1"/>';
      s += slash(dx2, ey, ink) + slash(dx2, ey + eh, ink);
      s += label(dx2 + 9, ey + eh / 2 + 4, d + ' cm', ink, 'start');
      s += '</svg>';
      drawing.innerHTML = s;

      function tick(a, b, c2, d2, stroke) { return '<line x1="' + a + '" y1="' + b + '" x2="' + c2 + '" y2="' + d2 + '" stroke="' + stroke + '" stroke-width=".8"/>'; }
      function slash(x, y, stroke) { return '<line x1="' + (x - 5) + '" y1="' + (y + 5) + '" x2="' + (x + 5) + '" y2="' + (y - 5) + '" stroke="' + stroke + '" stroke-width="1.2"/>'; }
      function label(x, y, text, fillColor, anchor, size, spacing) {
        return '<text x="' + x + '" y="' + y + '" text-anchor="' + (anchor || 'start') + '" font-family="ui-monospace, monospace" font-size="' + (size || 13) + '"' +
          (spacing ? ' letter-spacing="' + spacing + '"' : '') + ' fill="' + fillColor + '">' + text + '</text>';
      }
      function dimLine(x1, y1, x2, y2, text, stroke, vertical) {
        var out = '<line x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2 + '" stroke="' + stroke + '" stroke-width=".9"/>' + slash(x1, y1, stroke) + slash(x2, y2, stroke);
        if (vertical) out += '<g transform="translate(' + (x1 + 12) + ',' + ((y1 + y2) / 2) + ') rotate(-90)">' + label(0, 4, text, stroke, 'middle', 11) + '</g>';
        else out += label((x1 + x2) / 2, y1 - 5, text, stroke, 'middle', 11);
        return out;
      }
    }

    /* ---- sleeper weight → firmness hint ---- */
    function firmnessHint(kg) {
      if (!kg) return '';
      if (kg < 60) return D.strings.hintSoft;
      if (kg < 85) return D.strings.hintMedium;
      if (kg < 115) return D.strings.hintFirm;
      return D.strings.hintExtraFirm;
    }

    /* ---- render ---- */
    function render() {
      ['w', 'l', 'd'].forEach(readDim);

      var shapeInput = checked('cfg-shape');
      var shapeKey = shapeInput ? shapeInput.dataset.shape : 'rect';
      var shapeCfg = D.shapes.find(function (s) { return shapeInput && s.id === shapeInput.value; }) || { price: 0, variantId: null, label: '' };
      var isRound = shapeKey === 'round';
      var isModules = shapeKey === 'modules';

      readParams(shapeKey);

      /* round: one plan dimension */
      var lWrap = q('[data-dim="l"]'), wLabel = q('[data-dim="w"] .field__label');
      var lInput = q('[data-dim-input="l"]'), wInput = q('[data-dim-input="w"]');
      if (lWrap) lWrap.hidden = isRound;
      if (wLabel) wLabel.textContent = isRound ? (D.strings.diameter || 'Diameter') : D.strings.width;
      if (isRound && wInput && lInput && lInput.value !== wInput.value) { lInput.value = wInput.value; readDim('l'); }
      setText('[data-readout-label="w"]', isRound ? (D.strings.diameter || 'Diameter') : D.strings.width);
      var lRow = q('[data-readout-row="l"]'); if (lRow) lRow.hidden = isRound;

      /* split: only when the shape allows it; modules replace it */
      var splitWrap = q('[data-split-wrap]'), splitInput = q('input[name="cfg-split"]');
      if (splitWrap) splitWrap.hidden = isModules || isRound;
      var splitOn = !!(splitInput && splitInput.checked && !isModules && !isRound);
      var rightFirm = q('[data-firmness-right]'); if (rightFirm) rightFirm.hidden = !splitOn;
      var leftTitle = q('[data-firmness-left-title]'); if (leftTitle) leftTitle.hidden = !splitOn;
      var sleeperR = q('[data-sleeper-wrap="r"]'); if (sleeperR) sleeperR.hidden = !splitOn;

      /* sleeper weight hints */
      ['l', 'r'].forEach(function (side) {
        var inp = q('[data-sleeper="' + side + '"]'), hint = q('[data-sleeper-hint="' + side + '"]');
        if (inp && hint) hint.textContent = firmnessHint(parseFloat(inp.value));
      });

      setText('[data-readout="w"]', dims.w ? dims.w + ' cm' : '—');
      setText('[data-readout="l"]', dims.l ? dims.l + ' cm' : '—');
      setText('[data-readout="d"]', dims.d ? dims.d + ' cm' : '—');

      draw(dims.w, dims.l, dims.d, shapeKey);

      var allValid = valid.w && valid.l && valid.d;
      var foamInput = checked('cfg-foam');
      var foamValue = foamInput ? foamInput.value : null;
      var foam = foamCfg(foamValue);

      /* weight + delivery readouts */
      var est = allValid ? estimate(dims.w, dims.l, dims.d, foam, shapeKey) : null;
      setText('[data-readout="a"]', est ? est.areaM2.toFixed(2) + ' m²' : '—');
      setText('[data-readout="kg"]', est ? est.kg + ' kg' + (est.pieces > 1 ? ' · ' + est.pieces + ' ' + D.strings.pieces : '') : '—');
      setText('[data-readout="ship"]', est ? D.strings['ship_' + est.method] : '—');
      var liftWarn = q('[data-lift-warn]');
      if (liftWarn) liftWarn.hidden = !(est && est.kg > LIFT_BED_WARN_KG && (D.hasLightFoam && foam.light !== true));

      var band = allValid ? bandFor(dims.w, dims.l) : null;
      var tier = allValid ? depthTierFor(dims.d) : null;
      var lenTier = allValid ? lengthTierFor(dims.l) : null;

      if (band && tier) {
        root.querySelectorAll('[data-foam-price]').forEach(function (el) {
          var v = findVariant(band.value, el.getAttribute('data-foam-price'), tier.value);
          el.textContent = v ? money(v.price) : '—';
        });
        root.querySelectorAll('[data-band-cell]').forEach(function (cell) {
          var parts = cell.getAttribute('data-band-cell').split('|');
          var v = findVariant(parts[0], parts[1], tier.value);
          cell.textContent = v ? money(v.price) : '—';
        });
        root.querySelectorAll('[data-band-row]').forEach(function (row) {
          row.setAttribute('data-active', String(row.getAttribute('data-band-row') === band.value));
        });
        bandPill.hidden = false; bandPill.textContent = band.value;
      } else { bandPill.hidden = true; }

      var baseVariant = (band && tier && foamValue) ? findVariant(band.value, foamValue, tier.value) : null;

      var lines = [], total = 0;
      if (baseVariant) { lines.push([fill(D.strings.basePrice, { '__L__': band.value, '__F__': foamValue.toLowerCase() }), baseVariant.price]); total += baseVariant.price; }
      if (lenTier && lenTier.price > 0) { lines.push([lenTier.label, lenTier.price]); total += lenTier.price; }
      if (shapeCfg.price > 0) { lines.push([D.strings.shape + ' — ' + shapeCfg.label.toLowerCase(), shapeCfg.price]); total += shapeCfg.price; }
      if (isModules && D.modulePrice && params.mods) { var mp = D.modulePrice * (params.mods - 1); lines.push([D.strings.modules + ' × ' + params.mods, mp]); total += mp; }
      if (splitOn && splitInput) { var sp = parseInt(splitInput.dataset.surchargePrice || '0', 10); lines.push([D.strings.split, sp]); total += sp; }
      var coverInput = checked('cfg-cover');
      if (coverInput) { var cp = parseInt(coverInput.dataset.surchargePrice || '0', 10); lines.push([D.strings.cover + ' — ' + coverInput.value.toLowerCase(), cp]); total += cp; }
      form.querySelectorAll('input[name="cfg-extra"]:checked').forEach(function (extra) {
        var p = parseInt(extra.dataset.surchargePrice || '0', 10); lines.push([extra.value, p]); total += p;
      });
      lines.push([D.strings.delivery, 0]);

      linesEl.innerHTML = lines.map(function (line) {
        return '<div class="price-panel__line"><span>' + escapeHtml(line[0]) + '</span><span>' +
          (line[1] === 0 ? escapeHtml(D.strings.included) : money(line[1])) + '</span></div>';
      }).join('');

      var ackOk = !ack || ack.checked;
      if (!allValid) { totalEl.textContent = '—'; errorEl.textContent = ''; submit.disabled = true; }
      else if (!baseVariant) { totalEl.textContent = '—'; errorEl.textContent = D.strings.unavailable; submit.disabled = true; }
      else if (!baseVariant.available) { totalEl.textContent = money(total); errorEl.textContent = D.strings.unavailable; submit.disabled = true; }
      else if (!ackOk) { totalEl.textContent = money(total); errorEl.textContent = D.strings.ackRequired; submit.disabled = true; }
      else { totalEl.textContent = money(total); errorEl.textContent = ''; submit.disabled = false; }

      root.dataset.baseVariant = baseVariant ? baseVariant.id : '';
      root._est = est; root._lenTier = lenTier; root._splitOn = splitOn; root._shapeKey = shapeKey;
    }

    function escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    /* ---- vehicle / boat library ---- */
    if (vehicleSel) {
      vehicleSel.addEventListener('change', function () {
        var opt = vehicleSel.options[vehicleSel.selectedIndex];
        if (!opt || !opt.dataset.w) return;
        unit = 'cm';
        root.querySelectorAll('[data-unit]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-unit') === 'cm')); });
        q('[data-dim-input="w"]').value = opt.dataset.w;
        q('[data-dim-input="l"]').value = opt.dataset.l;
        if (opt.dataset.d) q('[data-dim-input="d"]').value = opt.dataset.d;
        var shapeRadio = form.querySelector('input[name="cfg-shape"][data-shape="' + (opt.dataset.shape || 'rect') + '"]');
        if (shapeRadio) shapeRadio.checked = true;
        ['c', 'd', 'r', 'w2', 'nw', 'nh', 'tw'].forEach(function (k) {
          var inp = form.querySelector('[data-param="' + k + '"]');
          var dk = 'p' + k.charAt(0).toUpperCase() + k.slice(1); /* data-p-nw → dataset.pNw */
          if (inp && opt.dataset[dk]) inp.value = opt.dataset[dk];
        });
        render();
      });
    }

    /* ---- submit ---- */
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var baseId = root.dataset.baseVariant;
      if (!baseId) return;

      var bundleId = 'ms-' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e4).toString(36);
      var shapeInput = checked('cfg-shape');
      var shapeCfg = D.shapes.find(function (s) { return shapeInput && s.id === shapeInput.value; }) || {};
      var firmL = checked('cfg-firmness'), firmR = checked('cfg-firmness-r');
      var coverInput = checked('cfg-cover');
      var est = root._est, lenTier = root._lenTier, splitOn = root._splitOn, shapeKey = root._shapeKey;
      var S = D.strings;

      var p = {};
      p[S.madeToMeasure] = 'Yes';
      p[S.shape] = shapeCfg.label || '';
      p[S.width] = dims.w + ' cm';
      p[S.length] = dims.l + ' cm';
      p[S.depth] = dims.d + ' cm';
      if (shapeKey === 'corner') { p['C'] = params.c + ' cm'; p['D'] = params.d + ' cm'; p[S.cornerPos] = (checked('cfg-corner') || {}).value || ''; }
      if (shapeKey === 'cutout') { p[S.cutoutW] = params.nw + ' cm'; p[S.cutoutL] = params.nh + ' cm'; p[S.cornerPos] = (checked('cfg-corner') || {}).value || ''; }
      if (shapeKey === 'taper') p[S.footWidth] = params.tw + ' cm';
      if (shapeKey === 'rounded') p['R'] = params.r + ' cm';
      if (shapeKey === 'twowidth') { p[S.footWidth] = params.w2 + ' cm'; p[S.transitionAt] = params.c + ' cm'; }
      if (shapeKey === 'modules') p[S.modules] = String(params.mods);
      if (firmL) p[splitOn ? S.firmnessLeft : S.firmness] = firmL.value + ' (' + firmL.dataset.firmnessScale + '/10)';
      if (splitOn && firmR) p[S.firmnessRight] = firmR.value + ' (' + firmR.dataset.firmnessScale + '/10)';
      var sl = q('[data-sleeper="l"]'), sr = q('[data-sleeper="r"]');
      if (sl && sl.value) p[S.sleeperLeft] = sl.value + ' kg';
      if (splitOn && sr && sr.value) p[S.sleeperRight] = sr.value + ' kg';
      if (coverInput) p[S.cover] = coverInput.value;
      if (est) { p[S.estWeight] = est.kg + ' kg'; p[S.deliveryMethod] = S['ship_' + est.method]; }
      if (vehicleSel && vehicleSel.value) p[S.vehicle] = vehicleSel.options[vehicleSel.selectedIndex].text;
      var notes = q('[data-notes]');
      if (notes && notes.value.trim()) p[S.notes] = notes.value.trim().slice(0, 500);
      p[S.drawingConfirm] = S.drawingConfirmValue;
      p._bundle = bundleId;

      var items = [{ id: parseInt(baseId, 10), quantity: 1, properties: p }];
      function pushSurcharge(variantId, label) {
        if (!variantId) return;
        var props = { _parent: bundleId }; props[S.madeToMeasure] = label;
        items.push({ id: parseInt(variantId, 10), quantity: 1, properties: props });
      }
      if (shapeCfg.variantId) pushSurcharge(shapeCfg.variantId, shapeCfg.label);
      if (lenTier && lenTier.variantId) pushSurcharge(lenTier.variantId, lenTier.label);
      if (shapeKey === 'modules' && D.moduleVariantId && params.mods > 1) {
        var props = { _parent: bundleId }; props[S.madeToMeasure] = S.modules;
        items.push({ id: parseInt(D.moduleVariantId, 10), quantity: params.mods - 1, properties: props });
      }
      var splitInput = q('input[name="cfg-split"]');
      if (splitOn && splitInput && splitInput.dataset.surchargeVariant) pushSurcharge(splitInput.dataset.surchargeVariant, S.split);
      if (coverInput && coverInput.dataset.surchargeVariant) pushSurcharge(coverInput.dataset.surchargeVariant, coverInput.value);
      form.querySelectorAll('input[name="cfg-extra"]:checked').forEach(function (extra) { pushSurcharge(extra.dataset.surchargeVariant, extra.value); });

      if (window.themeAddToCart) window.themeAddToCart({ items: items }, submit);
    });

    /* ---- 24 h dispatch countdown (cut-off 15:00 Europe/London, Mon–Fri) ---- */
    var cutoffEl = root.querySelector('[data-cutoff]');
    function ukNow() {
      try {
        var parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour12: false, weekday: 'short', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date());
        var o = {}; parts.forEach(function (p) { o[p.type] = p.value; });
        return { day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(o.weekday), min: parseInt(o.hour, 10) % 24 * 60 + parseInt(o.minute, 10) };
      } catch (e) { var n = new Date(); return { day: n.getDay(), min: n.getHours() * 60 + n.getMinutes() }; }
    }
    function tickCutoff() {
      if (!cutoffEl || !D.strings.days) return;
      var now = ukNow(), CUT = 15 * 60, S = D.strings, days = S.days; /* days: today, tomorrow, Mon..Fri */
      var weekday = now.day >= 1 && now.day <= 5;
      if (weekday && now.min < CUT) {
        var left = CUT - now.min, h = Math.floor(left / 60), m = left % 60;
        var dispatch = now.day === 5 ? days[2] : days[1];
        cutoffEl.textContent = fill(S.cutoffCountdown, { '__T__': (h ? h + ' h ' : '') + m + ' min', '__DAY__': dispatch });
      } else {
        /* next working cut-off day */
        var next = now.day; do { next = (next + 1) % 7; } while (next === 0 || next === 6);
        cutoffEl.textContent = fill(S.cutoffClosed, { '__DAY__': days[next + 1] });
      }
    }
    tickCutoff(); setInterval(tickCutoff, 30000);

    /* ---- events ---- */
    form.addEventListener('input', render);
    form.addEventListener('change', render);
    root.querySelectorAll('[data-unit]').forEach(function (button) {
      button.addEventListener('click', function () {
        var next = button.getAttribute('data-unit');
        var currentCm = { w: dims.w, l: dims.l, d: dims.d };
        var currentParams = Object.assign({}, params);
        unit = next;
        root.querySelectorAll('[data-unit]').forEach(function (b) { b.setAttribute('aria-pressed', String(b === button)); });
        ['w', 'l', 'd'].forEach(function (key) {
          var input = form.querySelector('[data-dim-input="' + key + '"]');
          if (currentCm[key]) input.value = fromCm(currentCm[key]);
          input.step = unit === 'in' ? '0.5' : '1';
        });
        form.querySelectorAll('[data-param]').forEach(function (input) {
          var k = input.getAttribute('data-param');
          if (k !== 'mods' && currentParams[k]) input.value = fromCm(currentParams[k]);
        });
        render();
      });
    });

    /* ---- deep links from landing pages: ?shape=corner&w=130&l=196&d=15&foam=Comfort%20reflex&unit=in&vehicle=<handle> ---- */
    (function applyQuery() {
      var qs; try { qs = new URLSearchParams(window.location.search); } catch (e) { return; }
      if (!qs || ![].concat(['shape', 'w', 'l', 'd', 'foam', 'vehicle', 'unit', 'c', 'dd', 'tw', 'r', 'w2', 'nw', 'nh', 'split']).some(function (k) { return qs.has(k); })) return;
      if (qs.get('unit') === 'in') { var inBtn = root.querySelector('[data-unit="in"]'); if (inBtn) inBtn.click(); }
      if (qs.get('vehicle') && vehicleSel) {
        vehicleSel.value = qs.get('vehicle');
        if (vehicleSel.value) vehicleSel.dispatchEvent(new Event('change'));
      }
      var shapeRadio = qs.get('shape') && form.querySelector('input[name="cfg-shape"][data-shape="' + qs.get('shape') + '"]');
      if (shapeRadio) shapeRadio.checked = true;
      ['w', 'l', 'd'].forEach(function (k) { if (qs.get(k)) { var i = form.querySelector('[data-dim-input="' + k + '"]'); if (i) i.value = qs.get(k); } });
      /* depth is always given in cm in a deep link; convert when the page is in inches */
      if (unit === 'in' && qs.get('d')) { var di = form.querySelector('[data-dim-input="d"]'); if (di) di.value = Math.round(parseFloat(qs.get('d')) / 2.54 * 10) / 10; }
      var map = { c: 'c', dd: 'd', tw: 'tw', r: 'r', w2: 'w2', nw: 'nw', nh: 'nh' };
      Object.keys(map).forEach(function (k) { if (qs.get(k)) { var i = form.querySelector('[data-param="' + map[k] + '"]'); if (i) i.value = qs.get(k); } });
      if (qs.get('foam')) { var f = form.querySelector('input[name="cfg-foam"][value="' + qs.get('foam').replace(/"/g, '') + '"]'); if (f) f.checked = true; }
      if (qs.get('split') === '1') { var s = form.querySelector('input[name="cfg-split"]'); if (s) s.checked = true; }
      render();
      var cfg = root.closest('section') || root;
      if (cfg.scrollIntoView && qs.get('scroll') !== '0') setTimeout(function () { cfg.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50);
    })();

    render();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-configurator]').forEach(function (root) {
      if (root.dataset.configuratorReady) return;
      root.dataset.configuratorReady = 'true';
      init(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})();
